
import React, { useEffect, useState, useContext } from "react";
import { Input } from "../../src/components/ui/input.jsx";
import { Textarea } from "../../src/components/ui/textarea.jsx";
import { Label } from "../../src/components/ui/label.jsx";
import { Checkbox } from "../../src/components/ui/checkbox.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from "../../src/components/ui/select.jsx";
import { Button } from "../../src/components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../src/components/ui/card.jsx";
import { Upload, Download, AlertCircle, RefreshCw, Save } from "lucide-react";
import { Alert, AlertDescription } from "../../src/components/ui/alert.jsx";

import AddressAutocomplete from "./AddressAutocomplete.jsx";
import CouncilSelector from "./CouncilSelector.jsx";
import { ProfileContext } from "../../auth/ProfileContext";

// Bulk upload endpoint (always use our Azure Function)
const BULK_ENDPOINT = (import.meta?.env?.VITE_BULK_ENDPOINT) || '/api/evaluation/bulk';
const BULK_FUNCTION_KEY = (import.meta?.env?.VITE_BULK_FUNCTION_KEY) || '';
const buildBulkUrl = () => (BULK_FUNCTION_KEY ? `${BULK_ENDPOINT}?code=${encodeURIComponent(BULK_FUNCTION_KEY)}` : BULK_ENDPOINT);

// Mapping tables for different environments
const MAPPING_TABLES = {
  "dev": [
    {
      "name": "Blacktown City Council",
      "abbrev": "BCC",
      "dataverse_id": "36f5d5de-c239-ef11-a316-002248955826",
      "entra_id": "4819a244-9405-4202-af83-3055a0236624",
      "domains": ["https://www.blacktown.nsw.gov.au/Plan-build"]
    },
    {
      "name": "Cumberland City Council",
      "abbrev": "CCC",
      "dataverse_id": "90f5d5de-c239-ef11-a316-002248955826",
      "entra_id": "8384a975-6513-4f07-b3a4-86391e2773af",
      "domains": [
        "https://www.cumberland.nsw.gov.au/strategic-planning",
        "https://www.cumberland.nsw.gov.au/development-plans-policies-and-controls",
        "https://www.cumberland.nsw.gov.au/development-and-building",
        "https://www.cumberland.nsw.gov.au/heritage",
        "https://www.cumberland.nsw.gov.au/development-application-tracking"
      ]
    },
    {
      "name": "Wingecarribee Shire Council",
      "abbrev": "WSC",
      "dataverse_id": "d23a03e4-c239-ef11-a317-00224891f541",
      "entra_id": "7d0236c7-6e3d-4194-9963-460cf7c30ece",
      "domains": ["https://www.wsc.nsw.gov.au/Plan-and-Build"]
    }
  ],
  "test": [
    {
      "name": "Blacktown City Council",
      "abbrev": "BCC",
      "dataverse_id": "36f5d5de-c239-ef11-a316-002248955826",
      "entra_id": "9229371d-6b36-4845-86f8-774a4d1011a2",
      "domains": ["https://www.blacktown.nsw.gov.au/Plan-build"]
    },
    {
      "name": "Cumberland City Council",
      "abbrev": "CCC",
      "dataverse_id": "90f5d5de-c239-ef11-a316-002248955826",
      "entra_id": "6179b0c3-586a-4a38-80ef-1a9d16ac7462",
      "domains": [
        "https://www.cumberland.nsw.gov.au/strategic-planning",
        "https://www.cumberland.nsw.gov.au/development-plans-policies-and-controls",
        "https://www.cumberland.nsw.gov.au/development-and-building",
        "https://www.cumberland.nsw.gov.au/heritage",
        "https://www.cumberland.nsw.gov.au/development-application-tracking"
      ]
    },
    {
      "name": "Wingecarribee Shire Council",
      "abbrev": "WSC",
      "dataverse_id": "d23a03e4-c239-ef11-a317-00224891f541",
      "entra_id": "724016ab-e61d-4a80-bc72-210f2da2e59b",
      "domains": ["https://www.wsc.nsw.gov.au/Plan-and-Build"]
    }
  ],
  "prod": [
    {
      "name": "Blacktown City Council",
      "abbrev": "BCC",
      "dataverse_id": "placeholder1",
      "entra_id": "4339dd76-13e9-44a9-a5b5-a3f4297a1d62",
      "domains": ["https://www.blacktown.nsw.gov.au/Plan-build"]
    },
    {
      "name": "Cumberland City Council",
      "abbrev": "CCC",
      "dataverse_id": "placeholder2",
      "entra_id": "7aee9f1d-7085-4710-9cc8-e324ef7333be",
      "domains": [
        "https://www.cumberland.nsw.gov.au/strategic-planning",
        "https://www.cumberland.nsw.gov.au/development-plans-policies-and-controls",
        "https://www.cumberland.nsw.gov.au/development-and-building",
        "https://www.cumberland.nsw.gov.au/heritage",
        "https://www.cumberland.nsw.gov.au/development-application-tracking"
      ]
    },
    {
      "name": "Wingecarribee Shire Council",
      "abbrev": "WSC",
      "dataverse_id": "placeholder3",
      "entra_id": "2d65e94f-9753-42d3-840f-c7e47bb3f95e",
      "domains": ["https://www.wsc.nsw.gov.au/Plan-and-Build"]
    }
  ]
};

// Extract the first complete JSON value (object/array) from arbitrary text.
// Handles pretty-printed multi-line JSON followed by another JSON value.
function extractFirstJsonBlock(text) {
  if (typeof text !== 'string') return null;
  let inString = false;
  let escape = false;
  let depth = 0;
  let start = -1;
  let end = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (start === -1) {
      if (ch === '{' || ch === '[') {
        start = i;
        depth = 1;
        inString = false;
        escape = false;
        continue;
      }
      continue;
    }
    if (inString) {
      if (escape) {
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') {
      depth++;
      continue;
    }
    if (ch === '}' || ch === ']') {
      depth--;
      if (depth === 0) {
        end = i + 1; // include this closing brace/bracket
        break;
      }
    }
  }
  if (start !== -1 && end !== -1) {
    return text.slice(start, end);
  }
  return null;
}

// Minimal check to see if text looks like JSONL or JSON array
function normalizeBulkPayload(text) {
  if (!text || typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  try {
    // If it's valid JSON (object or array), pass through
    JSON.parse(trimmed);
    return trimmed;
  } catch {
    // Treat as JSONL (keep as newline-joined objects)
    return text
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(Boolean)
      .join('\n');
  }
}

export default function ConfigForm({ config, onChange, section, fetchSignal, onFetchSignalConsumed }) {
  const [environment, setEnvironment] = useState("dev");
  const [isLoadingPropertyData, setIsLoadingPropertyData] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [importError, setImportError] = useState(null);
  const profile = useContext(ProfileContext);

  // Bulk upload modal state
  const [showBulk, setShowBulk] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const handleChange = (path, value) => {
    const pathArray = path.split('.');
    onChange((prev) => {
      const newConfig = { ...prev };
    let current = newConfig;
    for (let i = 0; i < pathArray.length - 1; i++) {
        const key = pathArray[i];
        current[key] = { ...(current?.[key] || {}) };
        current = current[key];
    }
    current[pathArray[pathArray.length - 1]] = value;
      return newConfig;
    });
  };

  const getValue = (path) => {
    return path.split('.').reduce((obj, key) => obj?.[key], config) || '';
  };

  // Bulk upload handlers
  const handleOpenBulk = () => {
    setShowBulk(true);
    setBulkError(null);
    setBulkResult(null);
  };

  const handleCloseBulk = () => {
    setShowBulk(false);
    setBulkFile(null);
    setBulkBusy(false);
    setBulkError(null);
  };

  const handleBulkFileChange = (e) => {
    setBulkFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
    setBulkError(null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    try {
      setBulkBusy(true);
      setBulkError(null);
      setBulkResult(null);

      if (!bulkFile) {
        throw new Error('Please choose a .json or .jsonl file to upload.');
      }

      const text = await bulkFile.text();
      const payload = normalizeBulkPayload(text);
      if (!payload) throw new Error('The selected file is empty or invalid.');

      const url = buildBulkUrl();

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || data?.error || res.statusText || 'Bulk upload failed';
        throw new Error(msg);
      }
      setBulkResult(data);
    } catch (err) {
      setBulkError(err.message || String(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleCouncilSelect = (councilData) => {
    handleChange('org_id', councilData.entra_id);
    handleChange('project.council_name', councilData.name);
  };

  // Keep org_id in sync with selected council name + environment
  useEffect(() => {
    const selectedCouncilName = getValue('project.council_name');
    if (!selectedCouncilName) return;
    const maybeCouncil = (MAPPING_TABLES[environment] || []).find(c => c.name === selectedCouncilName);
    if (maybeCouncil?.entra_id && config.org_id !== maybeCouncil.entra_id) {
      handleChange('org_id', maybeCouncil.entra_id);
    }
  }, [environment, config?.project?.council_name]);

  // Populate user fields from decoded profile/token if present
  useEffect(() => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const base64 = accessToken?.split('.')[1];
      const claims = base64 ? JSON.parse(atob(base64)) : null;
      const emailFromToken = claims?.email || claims?.preferred_username || '';
      const givenName = claims?.given_name || '';
      const familyName = claims?.family_name || '';
      const oid = claims?.oid || claims?.sub || '';

      const email = profile?.profileData?.email || emailFromToken;
      const firstName = profile?.profileData?.first_name || givenName;
      const lastName = profile?.profileData?.last_name || familyName;
      const userId = profile?.profileData?.user_id || oid;

      if (email && !getValue('email')) handleChange('email', email);
      if (firstName && !getValue('first_name')) handleChange('first_name', firstName);
      if (lastName && !getValue('last_name')) handleChange('last_name', lastName);
      if (userId && !getValue('user_id')) handleChange('user_id', userId);
    } catch (e) {
      // no-op if token not parseable
    }
  }, [profile?.profileData]);

  // Trigger fetch when a preset pre-fills the address
  const lastFetchNonceRef = React.useRef(null);
  useEffect(() => {
    if (section !== 'project') return;
    if (!fetchSignal || !fetchSignal.nonce) return;
    if (lastFetchNonceRef.current === fetchSignal.nonce) return;
    if (!config.org_id || !fetchSignal.address) return;
    lastFetchNonceRef.current = fetchSignal.nonce;
    fetchPropertyData(fetchSignal.address);
    if (onFetchSignalConsumed) onFetchSignalConsumed();
  }, [fetchSignal, section, config.org_id]);

  const fetchPropertyData = async (address) => {
    if (!address || !config.org_id) {
      setApiError("Address and Organization ID are required to fetch property data.");
      return;
    }

    setIsLoadingPropertyData(true);
    setApiError(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setApiError('Authentication required. Please log in to continue.');
        setIsLoadingPropertyData(false);
        return;
      }

      const commonHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      // Property info and Lot width can run in parallel
      const [propertyResponse, lotWidthResponse] = await Promise.all([
        fetch('https://dev-api.myenterprise.ai/daisy/general/v1/public/nsw/property-info', {
        method: 'POST',
          headers: commonHeaders,
          body: JSON.stringify({ user_config: { project: { address } } }),
        }),
        fetch('https://dev-api.myenterprise.ai/daisy/general/v1/public/nsw/property-lot-width', {
          method: 'POST',
          headers: commonHeaders,
          body: JSON.stringify({ user_config: { org_id: config.org_id, project: { address } } }),
        }),
      ]);

      let propIdLocal = getValue('project.prop_id');

      // Handle property info
      if (propertyResponse.ok) {
        const propertyData = await propertyResponse.json();
        const project = propertyData?.user_config?.project;
        if (project) {
          if (Object.prototype.hasOwnProperty.call(project, 'minimum_lot_size')) {
            handleChange('project.minimum_lot_size', project.minimum_lot_size ?? '');
          }
          if (Object.prototype.hasOwnProperty.call(project, 'maximum_building_height')) {
            handleChange('project.maximum_building_height', project.maximum_building_height ?? '');
          }
          if (Object.prototype.hasOwnProperty.call(project, 'address') && project.address) {
            handleChange('project.address', project.address);
          }
        }
        const propIdFrom = propertyData?.user_config?.prop_id ?? propertyData?.prop_id;
        if (propIdFrom !== undefined && propIdFrom !== null) {
          propIdLocal = propIdFrom.toString();
          handleChange('project.prop_id', propIdLocal);
        }
      } else {
        const errorText = await propertyResponse.text();
        console.error('Property info API error:', errorText);
        setApiError(`Failed to fetch property info: ${propertyResponse.statusText}`);
      }

      // Handle lot width
      if (lotWidthResponse.ok) {
        const lotData = await lotWidthResponse.json();
        if (lotData.user_config?.project) {
          const project = lotData.user_config.project;
          if (project.lot_width) handleChange('project.lot_width', project.lot_width);
          if (typeof project.is_battle_axe_lot === 'boolean') handleChange('project.is_battle_axe_lot', project.is_battle_axe_lot);
          if (typeof project.is_corner_lot === 'boolean') handleChange('project.is_corner_lot', project.is_corner_lot);
        }
      } else {
        const errorText = await lotWidthResponse.text();
        console.error('Lot width API error:', errorText);
        setApiError(`Failed to fetch lot width: ${lotWidthResponse.statusText}`);
      }

      // Address-check and Property-precinct can run in parallel (both depend on propIdLocal)
      let shouldCallDensity = getValue('project.council_name') === 'Blacktown City Council';
      if (propIdLocal) {
        const addressCheckPromise = fetch('https://dev-api.myenterprise.ai/daisy/general/v1/public/nsw/address-check', {
          method: 'POST',
          headers: commonHeaders,
          body: JSON.stringify({
            user_config: {
              org_id: config.org_id,
              state: config.state || 'qa',
              project: { prop_id: propIdLocal.toString(), address },
            },
          }),
        });

        const precinctPromise = fetch('https://dev-api.myenterprise.ai/daisy/general/v1/public/nsw/property-precinct', {
          method: 'POST',
          headers: commonHeaders,
          body: JSON.stringify({
            user_config: { org_id: config.org_id, project: { prop_id: propIdLocal.toString() } },
          }),
        });

        const [addressCheckResponse, precinctResponse] = await Promise.all([
          addressCheckPromise,
          precinctPromise,
        ]);

        // Handle precinct
        if (precinctResponse.ok) {
          const precinctData = await precinctResponse.json();
          if (precinctData.precinct?.value) {
            handleChange('project.active_precinct', precinctData.precinct.value);
          }
        } else {
          const errorText = await precinctResponse.text();
          console.error('Precinct API error:', errorText);
          setApiError(`Failed to fetch precinct data: ${precinctResponse.statusText}`);
        }

        // Handle address check
        if (addressCheckResponse.ok) {
          const addressData = await addressCheckResponse.json();
          if (addressData?.council_name) {
            shouldCallDensity = addressData.council_name === 'Blacktown City Council';
          }
          if (addressData.council_name) handleChange('project.council_name', addressData.council_name);
          if (addressData.lot_size) handleChange('project.lot_size', addressData.lot_size);
          if (addressData.zone) handleChange('project.zone', addressData.zone);
          if (addressData.zone_description) handleChange('project.zone_description', addressData.zone_description);
          if (typeof addressData.bushfire_restrictions === 'boolean') {
            handleChange('project.is_bushfire_zone', addressData.bushfire_restrictions ? 'yes' : 'no');
          }
          if (typeof addressData.flood_restrictions === 'boolean') {
            handleChange('project.is_flood_zone', addressData.flood_restrictions ? 'yes' : 'no');
          }
          if (typeof addressData.heritage_restrictions === 'boolean') {
            handleChange('project.is_heritage_zone', addressData.heritage_restrictions ? 'yes' : 'no');
          }
        } else {
          const errorText = await addressCheckResponse.text();
          console.error('Address check API error:', errorText);
          setApiError(`Failed to fetch address check data: ${addressCheckResponse.statusText}`);
        }

        // Density lookup API call (Blacktown only), run after address-check result
        if (shouldCallDensity) {
        const densityResponse = await fetch('https://dev-api.myenterprise.ai/daisy/general/v1/public/nsw/density-lookup', {
          method: 'POST',
            headers: commonHeaders,
          body: JSON.stringify({
            user_config: {
              org_id: config.org_id,
                project: { prop_id: Number.isNaN(Number(propIdLocal)) ? propIdLocal : Number(propIdLocal) },
              },
            }),
        });

        if (densityResponse.ok) {
          const densityData = await densityResponse.json();
          if (densityData.type) {
            handleChange('project.dwelling_density', densityData.type);
          }
        } else {
          const errorText = await densityResponse.text();
            console.error('Density lookup API error:', errorText);
          setApiError(`Failed to fetch density data: ${densityResponse.statusText}`);
          }
        }
      }

    } catch (error) {
      setApiError(`Error fetching property data: ${error.message}`);
      console.error('Property data fetch error:', error);
    }

    setIsLoadingPropertyData(false);
  };

  const handleAddressChange = (address) => {
    handleChange('project.address', address);
    // Clear previous errors on new address input; fetching will occur on submit
    if (address && !config.org_id) {
      setApiError('Please select an Organization (Council) before entering an address.');
    } else {
      setApiError(null);
    }
  };

  const handleAddressSubmit = () => {
    const address = getValue('project.address');
    if (!address) return;
    if (!config.org_id) {
      // Try to derive org_id from selected council name in current environment
      const selectedCouncilName = getValue('project.council_name');
      const maybeCouncil = (MAPPING_TABLES[environment] || []).find(c => c.name === selectedCouncilName);
      if (maybeCouncil?.entra_id) {
        handleChange('org_id', maybeCouncil.entra_id);
      } else {
        setApiError('Please select an Organization (Council) before submitting an address.');
        return;
      }
    }
    setApiError(null); 
      fetchPropertyData(address);
  };

  const handleImportJson = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.jsonl,application/json,text/plain';

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;

        const useSingleObject = (obj) => {
          const userConfig = obj?.user_config || obj;
          if (userConfig && typeof userConfig === 'object' && !Array.isArray(userConfig)) {
            onChange(userConfig);
            setImportError(null);
            return true;
          }
          return false;
        };

        try {
          // Try JSON first
          try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
              if (parsed.length === 0) throw new Error('JSON array is empty.');
              if (parsed.length > 1) throw new Error('Multiple items detected. This screen imports a single user_config. Use Bulk Upload for multiple.');
              if (!useSingleObject(parsed[0])) throw new Error('Array item does not contain a user_config object.');
              return;
            } else if (parsed && typeof parsed === 'object') {
              if (!useSingleObject(parsed)) throw new Error('Object does not contain a valid user_config.');
              return;
            }
            throw new Error('Unsupported JSON format.');
          } catch (jsonErr) {
            // Try extracting a full JSON block from concatenated pretty JSON
            const firstBlock = extractFirstJsonBlock(text);
            if (firstBlock) {
              const firstObj = JSON.parse(firstBlock);
              if (!useSingleObject(firstObj)) throw new Error('First JSON block does not contain a user_config object.');
              const remainder = text.slice(text.indexOf(firstBlock) + firstBlock.length).trim();
              if (remainder) {
                setImportError('Imported the first JSON object from file. Multiple objects detected — use Bulk Upload for all.');
              } else {
                setImportError(null);
              }
              return;
            }
            // Fallback: JSONL first non-empty line
            const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
            if (lines.length === 0) throw jsonErr;
            const first = JSON.parse(lines[0]);
            if (!useSingleObject(first)) throw new Error('First JSONL line does not contain a user_config object.');
            if (lines.length > 1) {
              setImportError('Imported first record from JSONL. Multiple items detected — use Bulk Upload for full import.');
            } else {
              setImportError(null);
            }
          }
        } catch (error) {
          setImportError(`Invalid JSON file: ${error.message}`);
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  const handlePasteJson = async () => {
    try {
      let text = await navigator.clipboard.readText();

      // Normalize clipboard text: strip BOM & zero-width spaces
      if (text && typeof text === 'string') {
        text = text.replace(/^\uFEFF/, '') // BOM
                   .replace(/[\u200B-\u200D\u2060\u2061\u180E]/g, ''); // zero-width
      }

      const useSingleObject = (obj) => {
        const userConfig = obj?.user_config || obj;
        if (userConfig && typeof userConfig === 'object' && !Array.isArray(userConfig)) {
          onChange(userConfig);
          setImportError(null);
          return true;
        }
        return false;
      };

      // 0) Try to extract the first complete JSON block from arbitrary/concatenated text **first**
      const firstBlock = extractFirstJsonBlock(text);
      if (firstBlock) {
        try {
          const firstObj = JSON.parse(firstBlock);
          if (!useSingleObject(firstObj)) throw new Error('First JSON block does not contain a user_config object.');

          // Warn if there appears to be more JSON after the first block
          const remainder = text.slice(text.indexOf(firstBlock) + firstBlock.length).trim();
          if (remainder.length > 0) {
            setImportError('Imported the first JSON object from clipboard. Multiple objects detected — use Bulk Upload for all.');
          } else {
            setImportError(null);
          }
          return;
        } catch (e) {
          // Fall through to whole-text/JSONL attempts
        }
      }

      // 1) Try direct JSON parse of the whole text
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          if (parsed.length === 0) throw new Error('JSON array is empty.');
          if (parsed.length > 1) throw new Error('Multiple items detected. Use Bulk Upload for multiple.');
          if (!useSingleObject(parsed[0])) throw new Error('Array item does not contain a user_config object.');
          return;
        } else if (parsed && typeof parsed === 'object') {
          if (!useSingleObject(parsed)) throw new Error('Object does not contain a valid user_config.');
          return;
        }
        throw new Error('Unsupported JSON format.');
      } catch (directErr) {
        // 2) Fallback: treat as JSONL (use the first non-empty line)
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        if (lines.length === 0) throw directErr;
        try {
          const first = JSON.parse(lines[0]);
          if (!useSingleObject(first)) throw new Error('First JSONL line does not contain a user_config object.');
          if (lines.length > 1) {
            setImportError('Imported first record from JSONL. Multiple items detected — use Bulk Upload for full import.');
          } else {
            setImportError(null);
          }
          return;
        } catch (jsonlErr) {
          throw directErr; // report the original error for clarity
        }
      }
    } catch (error) {
      setImportError(`Invalid JSON in clipboard: ${error.message}`);
    }
  };

  const generateUUID = (field) => {
    let prefix = '';
    if (field === 'project_id') prefix = 'proj_';
    if (field === 'conversation_id') prefix = 'conv_';
    if (field === 'user_id') prefix = 'user_';
    handleChange(field, `${prefix}${crypto.randomUUID()}`);
  };

  if (section === "user") {
    return (
      <div className="space-y-6">
        {/* Import/Export Section */}
        <Card className="bg-blue-50 border-blue-200 dark:bg-slate-900/50 dark:border-slate-700" data-tour="user-import-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-blue-800 dark:text-slate-100">Import Existing Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Button
                variant="outline"
                onClick={handleImportJson}
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload JSON File
              </Button>
              <Button
                variant="outline"
                onClick={handlePasteJson}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Paste from Clipboard
              </Button>
              <Button
                variant="default"
                onClick={handleOpenBulk}
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </Button>
            </div>
            {importError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{importError}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {showBulk && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-slate-900 shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Bulk Upload to Function</h3>
                <Button variant="ghost" onClick={handleCloseBulk}>Close</Button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-slate-700 dark:text-slate-200 font-medium">Destination</Label>
                  <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">
                    Files will be uploaded to <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{buildBulkUrl()}</code>
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    This always uses our Function endpoint. You can override it via <code>VITE_BULK_ENDPOINT</code> and optionally set <code>VITE_BULK_FUNCTION_KEY</code>.
                  </p>
                </div>

                <div>
                  <Label htmlFor="bulk-file" className="text-slate-700 dark:text-slate-200 font-medium">JSON/JSONL File</Label>
                  <input
                    id="bulk-file"
                    type="file"
                    accept=".json,.jsonl,application/json,text/plain"
                    onChange={handleBulkFileChange}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Accepts a JSON array, a single JSON object, or JSONL (one object per line).
                  </p>
                </div>

                {bulkError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{bulkError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex items-center gap-3">
                  <Button onClick={handleBulkUpload} disabled={bulkBusy} className="bg-emerald-600 hover:bg-emerald-700">
                    {bulkBusy ? 'Uploading…' : 'Start Upload'}
                  </Button>
                  <Button variant="outline" onClick={() => setBulkResult(null)} disabled={bulkBusy}>Clear Result</Button>
                </div>

                {bulkResult && (
                  <div className="mt-4">
                    <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/60">
                      <CardHeader>
                        <CardTitle className="text-base dark:text-slate-100">Bulk Upload Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <pre className="text-xs bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-200 p-3 rounded overflow-auto max-h-64">{JSON.stringify(bulkResult, null, 2)}</pre>
                        {Array.isArray(bulkResult?.results) && bulkResult.results.some(r => r.status === 'error') && (
                          <Alert variant="destructive" className="mt-3">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              Some items failed validation (e.g., missing <code>org_id</code> or <code>project.address</code>). Fix and re-upload.
                            </AlertDescription>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Environment Selection */}
        <div>
          <Label htmlFor="environment" className="text-slate-700 dark:text-slate-200 font-medium">Environment</Label>
          <Select value={environment} onValueChange={setEnvironment}>
            <SelectTrigger>
              <SelectValue placeholder="Select environment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dev">Development</SelectItem>
              <SelectItem value="test">Test</SelectItem>
              <SelectItem value="prod">Production</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Council Selection */}
        <CouncilSelector
          councils={MAPPING_TABLES[environment]}
          selectedCouncil={getValue('project.council_name')}
          onSelect={handleCouncilSelect}
        />

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="state" className="text-slate-700 dark:text-slate-200 font-medium">Program State*</Label>
            <Select value={getValue('state')} onValueChange={(value) => handleChange('state', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select program state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qa">QA</SelectItem>
                <SelectItem value="planning_pathway">Planning Pathway</SelectItem>
                <SelectItem value="document_checklist">Document Checklist</SelectItem>
                <SelectItem value="application">Application</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="next_action" className="text-slate-700 dark:text-slate-200 font-medium">Next Action</Label>
            <Select value={getValue('workflow.next_action')} onValueChange={(value) => handleChange('workflow.next_action', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select next action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>None</SelectItem> 
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="enter_address">Enter Address</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <Label htmlFor="project_id" className="text-slate-700 dark:text-slate-200 font-medium">Project ID*</Label>
            <Input
              id="project_id"
              value={getValue('project_id')}
              onChange={(e) => handleChange('project_id', e.target.value)}
              placeholder="e.g., proj_..."
              className="pr-10"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-6 h-8 w-8 text-slate-500 hover:text-slate-700"
              onClick={() => generateUUID('project_id')}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          <div className="relative">
            <Label htmlFor="conversation_id" className="text-slate-700 dark:text-slate-200 font-medium">Conversation ID*</Label>
            <Input
              id="conversation_id"
              value={getValue('conversation_id')}
              onChange={(e) => handleChange('conversation_id', e.target.value)}
              placeholder="e.g., conv_..."
              className="pr-10"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-6 h-8 w-8 text-slate-500 hover:text-slate-700"
              onClick={() => generateUUID('conversation_id')}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          <div className="relative">
            <Label htmlFor="user_id" className="text-slate-700 dark:text-slate-200 font-medium">User ID*</Label>
            <Input
              id="user_id"
              value={getValue('user_id')}
              onChange={(e) => handleChange('user_id', e.target.value)}
              placeholder="e.g., user_..."
              className="pr-10"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-6 h-8 w-8 text-slate-500 hover:text-slate-700"
              onClick={() => generateUUID('user_id')}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="first_name" className="text-slate-700 dark:text-slate-200 font-medium">First Name</Label>
            <Input
              id="first_name"
              data-tour="user-first-name"
              value={getValue('first_name')}
              onChange={(e) => handleChange('first_name', e.target.value)}
              placeholder="e.g., John"
            />
          </div>
          <div>
            <Label htmlFor="last_name" className="text-slate-700 dark:text-slate-200 font-medium">Last Name</Label>
            <Input
              id="last_name"
              value={getValue('last_name')}
              onChange={(e) => handleChange('last_name', e.target.value)}
              placeholder="e.g., Smith"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="text-slate-700 dark:text-slate-200 font-medium">Email</Label>
          <Input
            id="email"
            type="email"
            value={getValue('email')}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="e.g., john.smith@example.com"
          />
        </div>

        <div>
          <Label htmlFor="country" className="text-slate-700 dark:text-slate-200 font-medium">Country</Label>
          <Input
            id="country"
            value={getValue('country')}
            onChange={(e) => handleChange('country', e.target.value)}
            placeholder="Australia"
          />
        </div>

        <div className="space-y-4">
          <h4 className="font-semibold text-slate-800 dark:text-slate-100">Workflow</h4>
          <div>
            <Label htmlFor="intent" className="text-slate-700 dark:text-slate-200 font-medium">Intent</Label>
            <Input
              id="intent"
              value={getValue('workflow.intent')}
              onChange={(e) => handleChange('workflow.intent', e.target.value)}
              placeholder="e.g., property_development_inquiry"
            />
          </div>
        </div>

        {apiError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{apiError}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  if (section === "project") {
    return (
      <div className="space-y-6">
        <div>
          <Label htmlFor="address" className="text-slate-700 dark:text-slate-200 font-medium">Address</Label>
          {!config.org_id && !apiError && (
            <div className="mt-2">
              <Alert className="dark:bg-slate-900/60 dark:border-slate-700">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="dark:text-slate-300">
                  Please select an Organization (Council) above before submitting an address.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {apiError && (
            <div className="mt-2">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            </div>
          )}
          <div
            className="flex items-center gap-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddressSubmit();
              }
            }}
          >
            <div className="relative flex-1">
            <AddressAutocomplete
              value={getValue('project.address')}
              onChange={handleAddressChange}
              placeholder="e.g., 123 Main Street, Parramatta NSW 2150"
                busy={isLoadingPropertyData}
            />
              </div>
            <Button
              type="button"
              onClick={handleAddressSubmit}
              disabled={!getValue('project.address') || isLoadingPropertyData || !config.org_id}
            >
              Submit
            </Button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Click Submit to fetch and populate property details
          </p>
        </div>

        {/* Auto-populated fields from API */}
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="prop_id" className="text-slate-700 dark:text-slate-200 font-medium">Property ID</Label>
            <Input
              id="prop_id"
              value={getValue('project.prop_id')}
              onChange={(e) => handleChange('project.prop_id', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="zone" className="text-slate-700 dark:text-slate-200 font-medium">Zone</Label>
            <Input
              id="zone"
              value={getValue('project.zone')}
              onChange={(e) => handleChange('project.zone', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="active_precinct" className="text-slate-700 dark:text-slate-200 font-medium">Active Precinct</Label>
            <Input
              id="active_precinct"
              value={getValue('project.active_precinct')}
              onChange={(e) => handleChange('project.active_precinct', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="zone_description" className="text-slate-700 dark:text-slate-200 font-medium">Zone Description</Label>
          <Textarea
            id="zone_description"
            value={getValue('project.zone_description')}
            onChange={(e) => handleChange('project.zone_description', e.target.value)}
            placeholder="Auto-filled from API"
            className="h-20 bg-slate-50 dark:bg-slate-900/60"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Council Name input, now not disabled */}
          <div>
            <Label htmlFor="council_name" className="text-slate-700 dark:text-slate-200 font-medium">Council Name</Label>
            <Input
              id="council_name"
              value={getValue('project.council_name')}
              onChange={(e) => handleChange('project.council_name', e.target.value)}
              placeholder="Auto-filled from selection"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="planning_pathway" className="text-slate-700 dark:text-slate-200 font-medium">Planning Pathway</Label>
            <Select
              value={getValue('project.planning_pathway')}
              onValueChange={(value) => handleChange('project.planning_pathway', value)}
            >
              <SelectTrigger id="planning_pathway">
                <SelectValue placeholder="Select a planning pathway" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>General</SelectLabel>
                  <SelectItem value="House">House</SelectItem>
                  <SelectItem value="Detached Studio">Detached Studio</SelectItem>
                  <SelectItem value="Secondary Dwelling (Granny flat)">Secondary Dwelling (Granny flat)</SelectItem>
                  <SelectItem value="Privacy screen (Minor Works)">Privacy screen (Minor Works)</SelectItem>
                  <SelectItem value="Aerials, antennae and communication dishes (Minor Works)">Aerials, antennae and communication dishes (Minor Works)</SelectItem>
                  <SelectItem value="Land Fill">Land Fill</SelectItem>
                  <SelectItem value="Driveway or Hard Stand Space (Minor Works)">Driveway or Hard Stand Space (Minor Works)</SelectItem>
                  <SelectItem value="Earthworks">Earthworks</SelectItem>
                  <SelectItem value="Demolition">Demolition</SelectItem>
                  <SelectItem value="Awning, blinds or canopy (Minor Works)">Awning, blinds or canopy (Minor Works)</SelectItem>
                  <SelectItem value="Farm Building">Farm Building</SelectItem>
                  <SelectItem value="Dual Occupancies">Dual Occupancies</SelectItem>
                  <SelectItem value="Excavation">Excavation</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Garages and Carports</SelectLabel>
                  <SelectItem value="Detached Garage">Detached Garage</SelectItem>
                  <SelectItem value="Attached Garage">Attached Garage</SelectItem>
                  <SelectItem value="Attached Carport">Attached Carport</SelectItem>
                  <SelectItem value="Detached Carport">Detached Carport</SelectItem>
                  <SelectItem value="Carport (Minor Works)">Carport (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Terraces</SelectLabel>
                  <SelectItem value="Attached Terrace">Attached Terrace</SelectItem>
                  <SelectItem value="Detached Terrace">Detached Terrace</SelectItem>
                  <SelectItem value="Terrace (Minor Works)">Terrace (Minor Works)</SelectItem>
                  <SelectItem value="Screened Terrace (Minor Works)">Screened Terrace (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Stairways</SelectLabel>
                  <SelectItem value="Internal Stairways">Internal Stairways</SelectItem>
                  <SelectItem value="External Stairways">External Stairways</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Verandahs</SelectLabel>
                  <SelectItem value="Attached Verandah">Attached Verandah</SelectItem>
                  <SelectItem value="Detached Verandah">Detached Verandah</SelectItem>
                  <SelectItem value="Verandah (Minor Works)">Verandah (Minor Works)</SelectItem>
                  <SelectItem value="Screened Verandah (Minor Works)">Screened Verandah (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Patios</SelectLabel>
                  <SelectItem value="Attached Patio">Attached Patio</SelectItem>
                  <SelectItem value="Detached Patio">Detached Patio</SelectItem>
                  <SelectItem value="Patio (Minor Works)">Patio (Minor Works)</SelectItem>
                  <SelectItem value="Screened Patio (Minor Works)">Screened Patio (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Alterations</SelectLabel>
                  <SelectItem value="Internal Alterations">Internal Alterations</SelectItem>
                  <SelectItem value="External Alterations">External Alterations</SelectItem>
                  <SelectItem value="Alterations of Dual Occupancies">Alterations of Dual Occupancies</SelectItem>
                  <SelectItem value="Additions of Dual Occupancies">Additions of Dual Occupancies</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Balconies</SelectLabel>
                  <SelectItem value="Attached Balcony">Attached Balcony</SelectItem>
                  <SelectItem value="Detached Balcony">Detached Balcony</SelectItem>
                  <SelectItem value="Balcony (Minor Works)">Balcony (Minor Works)</SelectItem>
                  <SelectItem value="Screened Balcony (Minor Works)">Screened Balcony (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Pergolas</SelectLabel>
                  <SelectItem value="Attached Pergola">Attached Pergola</SelectItem>
                  <SelectItem value="Detached Pergola">Detached Pergola</SelectItem>
                  <SelectItem value="Pergola (Minor Works)">Pergola (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Subdivisions</SelectLabel>
                  <SelectItem value="Torrens Title Subdivision for dual occupancy or attached dwellings">Torrens Title Subdivision for dual occupancy or attached dwellings</SelectItem>
                  <SelectItem value="Strata Subdivision for dual occupancies, multi-dwelling houses, attached dwellings or apartments">Strata Subdivision for dual occupancies, multi-dwelling houses, attached dwellings or apartments</SelectItem>
                  <SelectItem value="Subdivision (Not Buildings)">Subdivision (Not Buildings)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Decks</SelectLabel>
                  <SelectItem value="Attached Deck">Attached Deck</SelectItem>
                  <SelectItem value="Detached Deck">Detached Deck</SelectItem>
                  <SelectItem value="Deck (Minor Works)">Deck (Minor Works)</SelectItem>
                  <SelectItem value="Screened Deck (Minor Works)">Screened Deck (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Sheds</SelectLabel>
                  <SelectItem value="Detached Shed">Detached Shed</SelectItem>
                  <SelectItem value="Farm Shed">Farm Shed</SelectItem>
                  <SelectItem value="Garden Shed (Minor Works)">Garden Shed (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Rain Tanks</SelectLabel>
                  <SelectItem value="Rain tank - above ground (Minor Works)">Rain tank - above ground (Minor Works)</SelectItem>
                  <SelectItem value="Rain tank - below ground (Minor Works)">Rain tank - below ground (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Pools</SelectLabel>
                  <SelectItem value="Swimming Pool">Swimming Pool</SelectItem>
                  <SelectItem value="Portable Swimming Pool">Portable Swimming Pool</SelectItem>
                  <SelectItem value="Portable Swimming Pool and spas and child-resistant barriers (Minor Works)">Portable Swimming Pool and spas and child-resistant barriers (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Fences</SelectLabel>
                  <SelectItem value="Fence (Minor Works)">Fence (Minor Works)</SelectItem>
                  <SelectItem value="Fence (for a swimming pool)">Fence (for a swimming pool)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Ventilators</SelectLabel>
                  <SelectItem value="Ventilators">Ventilators</SelectItem>
                  <SelectItem value="Ventilator (Minor Works)">Ventilator (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Skylights & Roof Windows</SelectLabel>
                  <SelectItem value="Skylight">Skylight</SelectItem>
                  <SelectItem value="Skylight (Minor Works)">Skylight (Minor Works)</SelectItem>
                  <SelectItem value="Roof Windows">Roof Windows</SelectItem>
                  <SelectItem value="Roof Windows (Minor Works)">Roof Windows (Minor Works)</SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Other</SelectLabel>
                  <SelectItem value="Attic Conversions">Attic Conversions</SelectItem>
                  <SelectItem value="Air-conditioning unit (Minor Works)">Air-conditioning unit (Minor Works)</SelectItem>
                  <SelectItem value="Cabana (Minor Works)">Cabana (Minor Works)</SelectItem>
                  <SelectItem value="Retaining Walls and Structural Support">Retaining Walls and Structural Support</SelectItem>
                  <SelectItem value="Cubby house (Minor Works)">Cubby house (Minor Works)</SelectItem>
                  <SelectItem value="Water features and ponds (Minor Works)">Water features and ponds (Minor Works)</SelectItem>
                  <SelectItem value="Street library (Minor Works)">Street library (Minor Works)</SelectItem>
                  <SelectItem value="Gazebo (Minor Works)">Gazebo (Minor Works)</SelectItem>
                  <SelectItem value="Landscaping Structure (Minor Works)">Landscaping Structure (Minor Works)</SelectItem>
                  <SelectItem value="Roller shutter doors adjoining lanes (Minor Works)">Roller shutter doors adjoining lanes (Minor Works)</SelectItem>
                  <SelectItem value="Garbage bin storage enclosure (Minor Works)">Garbage bin storage enclosure (Minor Works)</SelectItem>
                  <SelectItem value="Shade structures of canvas, fabric, mesh, or the like (Minor Works)">Shade structures of canvas, fabric, mesh, or the like (Minor Works)</SelectItem>
                  <SelectItem value="Barbecue or other outdoor cooking structure (Minor Works)">Barbecue or other outdoor cooking structure (Minor Works)</SelectItem>
                  <SelectItem value="Fernerie (Minor Works)">Fernerie (Minor Works)</SelectItem>
                  <SelectItem value="Flagpole (Minor Works)">Flagpole (Minor Works)</SelectItem>
                  <SelectItem value="Pathways and paving (Minor Works)">Pathways and paving (Minor Works)</SelectItem>
                  <SelectItem value="Clothes hoist or clothesline (Minor Works)">Clothes hoist or clothesline (Minor Works)</SelectItem>
                  <SelectItem value="Aviary (Minor Works)">Aviary (Minor Works)</SelectItem>
                  <SelectItem value="Greenhouse (Minor Works)">Greenhouse (Minor Works)</SelectItem>
                  <SelectItem value="Letterbox (Minor Works)">Letterbox (Minor Works)</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="application_name" className="text-slate-700 dark:text-slate-200 font-medium">Application Name</Label>
            <Input
              id="application_name"
              value={getValue('project.application_name')}
              onChange={(e) => handleChange('project.application_name', e.target.value)}
              placeholder="e.g., New Two-Story House"
            />
          </div>
          <div>
            <Label htmlFor="building_height" className="text-slate-700 dark:text-slate-200 font-medium">Building Height</Label>
            <Input
              id="building_height"
              value={getValue('project.building_height')}
              onChange={(e) => handleChange('project.building_height', e.target.value)}
              placeholder="e.g., 8.5m"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="lot_width" className="text-slate-700 dark:text-slate-200 font-medium">Lot Width</Label>
            <Input
              id="lot_width"
              value={getValue('project.lot_width')}
              onChange={(e) => handleChange('project.lot_width', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="lot_size" className="text-slate-700 dark:text-slate-200 font-medium">Lot Size</Label>
            <Input
              id="lot_size"
              value={getValue('project.lot_size')}
              onChange={(e) => handleChange('project.lot_size', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="minimum_lot_size" className="text-slate-700 dark:text-slate-200 font-medium">Minimum Lot Size</Label>
            <Input
              id="minimum_lot_size"
              value={getValue('project.minimum_lot_size')}
              onChange={(e) => handleChange('project.minimum_lot_size', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="maximum_building_height" className="text-slate-700 dark:text-slate-200 font-medium">Max Building Height</Label>
            <Input
              id="maximum_building_height"
              value={getValue('project.maximum_building_height')}
              onChange={(e) => handleChange('project.maximum_building_height', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="dwelling_density" className="text-slate-700 dark:text-slate-200 font-medium">Dwelling Density</Label>
            <Input
              id="dwelling_density"
              value={getValue('project.dwelling_density')}
              onChange={(e) => handleChange('project.dwelling_density', e.target.value)}
              placeholder="Auto-filled from API"
              className="bg-slate-50 dark:bg-slate-900/60"
            />
          </div>
          <div>
            <Label htmlFor="development_type" className="text-slate-700 dark:text-slate-200 font-medium">Development Type</Label>
            <Input
              id="development_type"
              value={getValue('project.development_type')}
              onChange={(e) => handleChange('project.development_type', e.target.value)}
              placeholder="e.g., Single dwelling house"
            />
          </div>
          <div>
            <Label htmlFor="project_size" className="text-slate-700 dark:text-slate-200 font-medium">Project Size</Label>
            <Input
              id="project_size"
              value={getValue('project.project_size')}
              onChange={(e) => handleChange('project.project_size', e.target.value)}
              placeholder="e.g., 250m²"
            />
          </div>
          {/* Empty div to maintain grid layout if only 3 items */}
          <div></div> 
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="front_setback" className="text-slate-700 dark:text-slate-200 font-medium">Front Setback</Label>
            <Input
              id="front_setback"
              value={getValue('project.front_setback')}
              onChange={(e) => handleChange('project.front_setback', e.target.value)}
              placeholder="e.g., 6m"
            />
          </div>
          <div>
            <Label htmlFor="side_setback" className="text-slate-700 dark:text-slate-200 font-medium">Side Setback</Label>
            <Input
              id="side_setback"
              value={getValue('project.side_setback')}
              onChange={(e) => handleChange('project.side_setback', e.target.value)}
              placeholder="e.g., 1.5m"
            />
          </div>
          <div>
            <Label htmlFor="rear_setback" className="text-slate-700 dark:text-slate-200 font-medium">Rear Setback</Label>
            <Input
              id="rear_setback"
              value={getValue('project.rear_setback')}
              onChange={(e) => handleChange('project.rear_setback', e.target.value)}
              placeholder="e.g., 6m"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-semibold text-slate-800 dark:text-slate-100">Property Features</h4>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_corner_lot"
                checked={getValue('project.is_corner_lot')}
                onCheckedChange={(checked) => handleChange('project.is_corner_lot', checked)}
              />
              <Label htmlFor="is_corner_lot" className="dark:text-slate-200">Corner Lot</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_battle_axe_lot"
                checked={getValue('project.is_battle_axe_lot')}
                onCheckedChange={(checked) => handleChange('project.is_battle_axe_lot', checked)}
              />
              <Label htmlFor="is_battle_axe_lot" className="dark:text-slate-200">Battle Axe Lot</Label>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="is_bushfire_zone" className="text-slate-700 dark:text-slate-200 font-medium">Bushfire Zone</Label>
            <Select value={getValue('project.is_bushfire_zone')} onValueChange={(value) => handleChange('project.is_bushfire_zone', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Auto-filled from API" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="is_flood_zone" className="text-slate-700 dark:text-slate-200 font-medium">Flood Zone</Label>
            <Select value={getValue('project.is_flood_zone')} onValueChange={(value) => handleChange('project.is_flood_zone', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Auto-filled from API" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="is_heritage_zone" className="text-slate-700 dark:text-slate-200 font-medium">Heritage Zone</Label>
            <Select value={getValue('project.is_heritage_zone')} onValueChange={(value) => handleChange('project.is_heritage_zone', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Auto-filled from API" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  if (section === "policies") {
    return (
      <div className="space-y-8">
        <div>
          <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-4">State Policies</h4>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="SEP" className="text-slate-700 dark:text-slate-200 font-medium">SEP</Label>
                <Input
                  id="SEP"
                  value={getValue('policies.state.SEP')}
                  onChange={(e) => handleChange('policies.state.SEP', e.target.value)}
                  placeholder="e.g., State Environmental Planning Policy"
                />
              </div>
              <div>
                <Label htmlFor="SEP_public_link" className="text-slate-700 dark:text-slate-200 font-medium">SEP Public Link</Label>
                <Input
                  id="SEP_public_link"
                  value={getValue('policies.state.SEP_public_link')}
                  onChange={(e) => handleChange('policies.state.SEP_public_link', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="pol_min_lot_size_state" className="text-slate-700 dark:text-slate-200 font-medium">Min Lot Size</Label>
                <Input
                  id="pol_min_lot_size_state"
                  value={getValue('policies.state.pol_min_lot_size')}
                  onChange={(e) => handleChange('policies.state.pol_min_lot_size', e.target.value)}
                  placeholder="e.g., 450m²"
                />
              </div>
              <div>
                <Label htmlFor="pol_max_FSR_state" className="text-slate-700 dark:text-slate-200 font-medium">Max FSR</Label>
                <Input
                  id="pol_max_FSR_state"
                  value={getValue('policies.state.pol_max_FSR')}
                  onChange={(e) => handleChange('policies.state.pol_max_FSR', e.target.value)}
                  placeholder="e.g., 0.5:1"
                />
              </div>
              <div>
                <Label htmlFor="pol_max_building_height_state" className="text-slate-700 dark:text-slate-200 font-medium">Max Building Height</Label>
                <Input
                  id="pol_max_building_height_state"
                  value={getValue('policies.state.pol_max_building_height')}
                  onChange={(e) => handleChange('policies.state.pol_max_building_height', e.target.value)}
                  placeholder="e.g., 8.5m"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-4">Local Policies</h4>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="DCP" className="text-slate-700 dark:text-slate-200 font-medium">DCP</Label>
                <Input
                  id="DCP"
                  value={getValue('policies.local.DCP')}
                  onChange={(e) => handleChange('policies.local.DCP', e.target.value)}
                  placeholder="e.g., Development Control Plan 2021"
                />
              </div>
              <div>
                <Label htmlFor="LEP" className="text-slate-700 dark:text-slate-200 font-medium">LEP</Label>
                <Input
                  id="LEP"
                  value={getValue('policies.local.LEP')}
                  onChange={(e) => handleChange('policies.local.LEP', e.target.value)}
                  placeholder="e.g., Local Environmental Plan 2011"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pol_land_zone" className="text-slate-700 dark:text-slate-200 font-medium">Land Zone</Label>
                <Input
                  id="pol_land_zone"
                  value={getValue('policies.local.pol_land_zone')}
                  onChange={(e) => handleChange('policies.local.pol_land_zone', e.target.value)}
                  placeholder="e.g., R2 Low Density Residential"
                />
              </div>
              <div>
                <Label htmlFor="pol_land_council" className="text-slate-700 dark:text-slate-200 font-medium">Land Council</Label>
                <Input
                  id="pol_land_council"
                  value={getValue('policies.local.pol_land_council')}
                  onChange={(e) => handleChange('policies.local.pol_land_council', e.target.value)}
                  placeholder="e.g., City of Parramatta Council"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pol_provisions" className="text-slate-700 dark:text-slate-200 font-medium">Provisions</Label>
              <Textarea
                id="pol_provisions"
                value={getValue('policies.local.pol_provisions')}
                onChange={(e) => handleChange('policies.local.pol_provisions', e.target.value)}
                placeholder="e.g., Special provisions for heritage conservation..."
                className="h-20"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
